import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  MdAdd,
  MdDeleteOutline,
  MdOutlinePayments,
  MdOutlineReceiptLong,
} from "react-icons/md";
import axios from "../../config/axiosInstance";
import { CLIENTS } from "../../config/apiConfig";
import { useApplication } from "../../contexts/ApplicationContext";
import { Badge, Card, ErrorBlock } from "./Primitives";
import { formatDate, formatMoney } from "./crmHelpers";

/*
  Cobrança da conta: histórico e lançamento manual.

  O lançamento manual existe para o caso que nenhum gateway cobre — a cliente
  paga por PIX direto, combinado por WhatsApp. Sem registrar, o dinheiro entra
  e o sistema continua tratando a conta como quem nunca pagou: ela cai na fila
  de conversão em vez da de renovação, e o funil conta um cadastro que não
  converteu.

  Duas ações separadas de propósito: **registrar o dinheiro** e **estender o
  acesso**. Um PIX de um mês atrasado deve entrar no histórico sem empurrar o
  vencimento — quem lança decide.
*/

const EXTENSOES = [
  { value: "none", label: "Não mexer no vencimento" },
  { value: "30", label: "+ 30 dias" },
  { value: "1m", label: "+ 1 mês" },
  { value: "3m", label: "+ 3 meses" },
  { value: "6m", label: "+ 6 meses" },
  { value: "12m", label: "+ 12 meses" },
];

const hojeIso = () => {
  const agora = new Date();
  // Componentes locais: `toISOString()` devolveria o dia em UTC e, de
  // madrugada, o campo abriria com a data de ontem.
  return `${agora.getFullYear()}-${String(agora.getMonth() + 1).padStart(2, "0")}-${String(
    agora.getDate()
  ).padStart(2, "0")}`;
};

const campoClass =
  "w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-pink-300 focus:ring-2 focus:ring-pink-100";

const BillingPanel = ({ companyId, paymentHistory = [], summary }) => {
  const queryClient = useQueryClient();
  const { isAdmin } = useApplication();

  const [aberto, setAberto] = useState(false);
  const [erro, setErro] = useState(null);
  const [form, setForm] = useState({
    value: "",
    paidAt: hojeIso(),
    billingType: "PIX",
    extend: "none",
    note: "",
  });

  const { data: opcoes } = useQuery(["clients", "payment-options"], async () => {
    const { data } = await axios.get(`${CLIENTS.LIST}/payment-options`);
    return data.data;
  });

  const manuais = useQuery(["clients", companyId, "manual-payments"], async () => {
    const { data } = await axios.get(`${CLIENTS.ONE}/${companyId}/payments`);
    return data.data;
  });

  const invalidar = () => {
    queryClient.invalidateQueries(["clients", companyId]);
    queryClient.invalidateQueries(["client", "detail", String(companyId)]);
    queryClient.invalidateQueries(["crm"]);
    queryClient.invalidateQueries(["companies"]);
  };

  const registrar = useMutation(
    () => axios.post(`${CLIENTS.ONE}/${companyId}/payments`, form),
    {
      onSuccess: () => {
        setErro(null);
        setAberto(false);
        setForm({
          value: "",
          paidAt: hojeIso(),
          billingType: "PIX",
          extend: "none",
          note: "",
        });
        invalidar();
      },
      onError: (error) =>
        setErro(
          error.response?.data?.message || "Não foi possível registrar o pagamento."
        ),
    }
  );

  const remover = useMutation(
    (paymentId) => axios.delete(`${CLIENTS.LIST}/payments/${paymentId}`),
    {
      onSuccess: invalidar,
      onError: (error) =>
        setErro(
          error.response?.data?.message || "Não foi possível remover o lançamento."
        ),
    }
  );

  const lancamentosManuais = manuais.data || [];

  return (
    <Card className="overflow-hidden">
      <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-slate-100 px-4 py-3">
        <div>
          <h2 className="flex items-center gap-1.5 text-sm font-bold text-slate-900">
            <MdOutlinePayments size={16} /> Cobrança
          </h2>
          <p className="text-[11px] text-slate-500">
            {summary?.approvedCount
              ? `${summary.approvedCount} pagamento(s) confirmado(s) · ${formatMoney(
                  summary.totalAmount
                )}`
              : "Nenhum pagamento confirmado ainda"}
          </p>
        </div>

        {isAdmin ? (
          <button
            type="button"
            onClick={() => setAberto((v) => !v)}
            className="inline-flex items-center gap-1 rounded-lg border border-pink-200 px-2.5 py-1 text-[11px] font-semibold text-pink-700 transition hover:bg-pink-50"
          >
            <MdAdd size={14} /> {aberto ? "Cancelar" : "Registrar pagamento"}
          </button>
        ) : null}
      </div>

      {erro ? (
        <div className="px-4 pt-3">
          <ErrorBlock message={erro} />
        </div>
      ) : null}

      {aberto ? (
        <div className="space-y-2 border-b border-slate-100 bg-slate-50/60 p-4">
          <p className="text-[11px] text-slate-600">
            Para quando a cliente paga por fora do sistema — PIX combinado,
            dinheiro, transferência. Entra no histórico e passa a contar como
            pagante.
          </p>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="mb-1 block text-[11px] font-semibold text-slate-600">
                Valor recebido
              </label>
              <input
                value={form.value}
                onChange={(e) => setForm((p) => ({ ...p, value: e.target.value }))}
                placeholder="149,70"
                inputMode="decimal"
                className={campoClass}
              />
            </div>
            <div>
              <label className="mb-1 block text-[11px] font-semibold text-slate-600">
                Data do pagamento
              </label>
              <input
                type="date"
                value={form.paidAt}
                max={hojeIso()}
                onChange={(e) => setForm((p) => ({ ...p, paidAt: e.target.value }))}
                className={campoClass}
              />
            </div>
            <div>
              <label className="mb-1 block text-[11px] font-semibold text-slate-600">
                Forma
              </label>
              <select
                value={form.billingType}
                onChange={(e) =>
                  setForm((p) => ({ ...p, billingType: e.target.value }))
                }
                className={campoClass}
              >
                {(opcoes?.billingTypes || [{ value: "PIX", label: "PIX" }]).map(
                  (item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  )
                )}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-[11px] font-semibold text-slate-600">
                Acesso
              </label>
              <select
                value={form.extend}
                onChange={(e) => setForm((p) => ({ ...p, extend: e.target.value }))}
                className={campoClass}
              >
                {EXTENSOES.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="mb-1 block text-[11px] font-semibold text-slate-600">
              Observação
            </label>
            <input
              value={form.note}
              onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))}
              placeholder="Ex.: pagou 3 meses adiantado, combinado no WhatsApp"
              className={campoClass}
            />
          </div>

          <button
            type="button"
            onClick={() => registrar.mutate()}
            disabled={!form.value.trim() || registrar.isLoading}
            className="w-full rounded-xl bg-pink-600 py-2 text-sm font-semibold text-white transition hover:bg-pink-700 disabled:opacity-40"
          >
            {registrar.isLoading ? "Registrando..." : "Registrar pagamento"}
          </button>
        </div>
      ) : null}

      {paymentHistory.length === 0 && lancamentosManuais.length === 0 ? (
        <p className="px-4 py-8 text-center text-xs text-slate-400">
          Nenhum pagamento registrado para esta conta.
        </p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {paymentHistory.slice(0, 12).map((pagamento) => {
            /*
              O lançamento manual é identificado pelo evento, e só ele pode ser
              apagado: um registro de gateway é espelho do provedor, e removê-lo
              faria o sistema divergir da fonte de verdade sem aviso.
            */
            const manual = pagamento.event === "MANUAL_ENTRY";
            const detalhe = manual
              ? lancamentosManuais.find((item) => item.id === pagamento.id)
              : null;

            return (
              <li
                key={pagamento.id}
                className="flex items-center gap-3 px-4 py-2.5"
              >
                <span className="shrink-0 text-slate-300">
                  <MdOutlineReceiptLong size={17} />
                </span>

                <div className="min-w-0 flex-1">
                  <p className="flex flex-wrap items-center gap-1.5 text-sm font-semibold text-slate-800">
                    {formatMoney(pagamento.value)}
                    {manual ? <Badge tone="warning">manual</Badge> : null}
                  </p>
                  <p className="truncate text-[11px] text-slate-500">
                    {formatDate(pagamento.paidAt)}
                    {pagamento.billingType ? ` · ${pagamento.billingType}` : ""}
                    {detalhe?.registeredBy ? ` · por ${detalhe.registeredBy}` : ""}
                    {detalhe?.note ? ` · ${detalhe.note}` : ""}
                  </p>
                </div>

                {manual && isAdmin ? (
                  <button
                    type="button"
                    title="Remover lançamento"
                    onClick={() => {
                      if (window.confirm("Remover este lançamento manual?")) {
                        remover.mutate(pagamento.id);
                      }
                    }}
                    className="shrink-0 rounded-lg p-1.5 text-slate-300 transition hover:bg-rose-50 hover:text-rose-600"
                  >
                    <MdDeleteOutline size={16} />
                  </button>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
};

export default BillingPanel;
